import React, { useMemo } from "react";
import { DateInput, Input } from "../../../../../../common/Input";
import { Select } from "../../../../../../common/Select";
import SectionBadge from "../../ui/SectionBadge";
import { SALARYTYPES } from "../../../../../../utils/enums";
import { MaintenanceSalaryCombinationV2PickerSelect } from "../../../../../../common/selectComponents/MaintenanceSalaryCombinationPicker";
import { TRANSACTION_SALARY_FILTER_MAP } from "../../../../filters/FilterConfig";

const CompensationTab = ({
  form,
  set,
  options,
  editable,
  errors,
  matchedRules, // salary
  requiredFields,
  loadingRates,
  accessRules
}) => {
  const hasValidationError = !!errors.maintenanceSalaryCombinationV2Id;

  const salaryTypeOptions = useMemo(() => {
    const allowMaintenanceSalaryCombination =
      accessRules?.allowMaintenanceSalaryCombinationV2Id === true;

    return options.salaryTypes.filter(
      salaryType =>
        salaryType.value !== SALARYTYPES.MAINTENANCESALARYCOMBINATION ||
        allowMaintenanceSalaryCombination
    );
  }, [options.salaryTypes, accessRules?.allowMaintenanceSalaryCombinationV2Id]);

  return (
    <div className="space-y-6">
      <div className="bg-gray-50 p-6 rounded-xl border border-gray-200">
        <SectionBadge label="Compensation Details" editable={editable} />
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-x-10 gap-y-4">

          {(accessRules?.allowDateHired ||
            accessRules?.allowPayType ||
            accessRules?.allowContractPeriod ||
            accessRules?.allowOvertime) && (
              <div className="space-y-4">
                {accessRules?.allowDateHired && (
                  <DateInput
                    label="Date Hired:"
                    value={form.dateHired || ""}
                    disabled={true}
                  />
                )}
                {accessRules?.allowPayType && (
                  <Select
                    label="Pay Type:"
                    options={options.payTypes}
                    value={form.payTypeId}
                    onChange={e => set('payTypeId', e.target.value)}
                    disabled={!editable}
                    placeholder="--Select Pay Type--"
                    isRequired={true}
                    error={!!errors.payTypeId}
                    errorMessage={errors.payTypeId}
                  />
                )}
                {accessRules?.allowContractPeriod && (
                  <div>
                    <p className="mb-1.5 block text-sm font-medium text-gray-600">Contract Period <span className="ml-1 text-red-500">*</span></p>
                    <div className="grid gap-2 sm:grid-cols-[1fr_auto_1fr] sm:items-center">
                      <DateInput
                        value={form.contractFrom || ""}
                        onChange={e => set('contractFrom', e.target.value)}
                        disabled={!editable}
                        isRequired={true}
                        error={!!errors.contractFrom}
                        errorMessage={errors.contractFrom}
                      />
                      <span className="text-sm text-gray-500 text-center">to</span>
                      <DateInput
                        value={form.contractTo || ""}
                        onChange={e => set('contractTo', e.target.value)}
                        disabled={!editable}
                        isRequired={true}
                        error={!!errors.contractTo}
                        errorMessage={errors.contractTo}
                      />
                    </div>
                  </div>
                )}
                {accessRules?.allowOvertime && (
                  <div className="mt-4 pt-4 border-t border-gray-200">
                    <label className={`flex items-center gap-3 ${editable ? "cursor-pointer" : "cursor-not-allowed opacity-60"}`}>
                      <input type="checkbox" checked={!!form.entitledToOvertime} onChange={e => set('entitledToOvertime', e.target.checked)} disabled={!editable} className="w-4 h-4 accent-blue-600" />
                      <span className="text-sm font-medium text-gray-700">Entitled to Overtime</span>
                    </label>
                  </div>
                )}
              </div>
            )}

          {(accessRules?.allowSalaryType ||
            (accessRules?.allowSalaryType && accessRules?.allowMaintenanceSalaryCombinationV2Id) ||
            accessRules?.allowSalaryRate) && (
              <div className="space-y-4">
                {accessRules?.allowSalaryType && (
                  <Select
                    label="Salary Type:"
                    options={salaryTypeOptions}
                    value={form.salaryType || ""}
                    onChange={e => {
                      set('salaryType', e.target.value);
                    }}
                    disabled={!editable}
                    placeholder="--Select Salary Type--"
                    isRequired={true}
                    error={!!errors.salaryType}
                    errorMessage={errors.salaryType}
                  />
                )}
                {accessRules?.allowSalaryType && accessRules?.allowMaintenanceSalaryCombinationV2Id && form.salaryType === SALARYTYPES.MAINTENANCESALARYCOMBINATION ? (
                  <div className="space-y-4">
                    <div className="relative">
                      <MaintenanceSalaryCombinationV2PickerSelect
                        label="Salary Rate Category"
                        context={form}
                        salaryType={form.salaryType}
                        effectiveDate={form.effectiveFrom}
                        keyMap={TRANSACTION_SALARY_FILTER_MAP}
                        value={form.maintenanceSalaryCombinationV2Id}
                        onSelect={(rule) => {
                          set('maintenanceSalaryCombinationV2Id', rule?.id || "");
                          set('rateAmount', rule?.dailyRate || "");
                        }}
                        onClear={() => {
                          set('maintenanceSalaryCombinationV2Id', "");
                          set('rateAmount', "");
                        }}
                        disabled={!editable}
                        isRequired={true}
                        error={!!errors.maintenanceSalaryCombinationV2Id}
                        errorMessage={errors.maintenanceSalaryCombinationV2Id}
                      />
                    </div>

                    {accessRules?.allowSalaryRate && (
                      <Input
                        label="Daily Rate (Calculated):"
                        value={form.rateAmount || ""}
                        isDisabled={true}
                        placeholder="Select a category above"
                        // We can also show error here if the rate is missing
                        error={hasValidationError && !form.rateAmount}
                      />
                    )}
                  </div>
                ) : (
                  <>
                    {accessRules?.allowSalaryRate && (
                      <Input
                        label="Rate Amount:"
                        type="number"
                        value={form.rateAmount || ""}
                        onChange={e => set('rateAmount', e.target.value)}
                        disabled={!editable || !form.salaryType}
                        placeholder="-- Enter Rate Amount --"
                        isRequired={true}
                        error={!!errors.rateAmount}
                        errorMessage={errors.rateAmount}
                      />
                    )}
                  </>
                )}
              </div>

            )}
        </div>
      </div>


    </div>
  );
};

export default CompensationTab;